'use client';

import NextImage, { ImageProps as NextImageProps } from 'next/image';
import React from 'react';

import { cn } from '@/lib/utils';
import { useBoolean } from '@/hooks/utils';
import { Icon } from './icon';
import { Skeleton } from './skeleton';

export interface ImageProps extends Omit<NextImageProps, 'onError' | 'onLoad'> {
  fallbackSrc?: string;
  className?: string;
  showSkeleton?: boolean;
  showBrokenImage?: boolean;
  containerClassName?: string;
  unoptimized?: boolean;
  quality?: number;
}

/**
 * Enhanced Image component with loading and error states.
 *
 * Loading: neutral skeleton (bg-surface-1, animate-pulse) until the image decodes.
 * Error: neutral placeholder (bg-surface-2 + image-not-supported icon). No alert chrome.
 * Loaded: fade in via opacity transition.
 */
const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      className,
      containerClassName,
      src,
      alt,
      fallbackSrc = '/images/fallback/placeholder.svg',
      showSkeleton = true,
      showBrokenImage = true,
      unoptimized = false,
      placeholder,
      blurDataURL,
      quality = 75,
       ...props
    },
    ref
  ) => {
    const { value: isLoading, onFalse: handleLoad } = useBoolean(true);
    const { value: hasError, onTrue: handleError } = useBoolean(false);

    // If src is null, undefined, or empty string, treat it as an error case
    // and use the fallback image instead
    const isSrcMissing = src === null || src === undefined || src === '';
    const effectiveSrc = isSrcMissing ? fallbackSrc : src;
    const effectiveHasError = isSrcMissing === false && hasError === true;


    if (showBrokenImage === true && effectiveHasError === true) {
      return (
        <div
          className={cn(
            'relative flex h-full w-full items-center justify-center bg-surface-2 text-on-surface-variant',
            containerClassName,
          )}
          role="img"
          aria-label={alt || ''}
        >
          <Icon
            icon="lucide:image-off"
            size={32}
            className="opacity-60"
            aria-hidden="true"
          />
        </div>
      );
    }

    return (
      <div className={cn('relative overflow-hidden', containerClassName)}>
        {isLoading === true && showSkeleton === true && placeholder !== 'blur' && (
          <Skeleton className="absolute inset-0 h-full w-full rounded-none bg-surface-1" />
        )}
        <NextImage
          ref={ref}
          {...props}
          src={isSrcMissing === true ? fallbackSrc : effectiveSrc}
          alt={alt || ''}
          className={cn(
            'transition-opacity duration-[var(--duration-medium-2)] ease-[var(--ease-emphasized)] motion-reduce:transition-none',
            isLoading === true ? 'opacity-0' : 'opacity-100',
            className,
          )}
          onError={handleError}
          onLoad={handleLoad}
          placeholder={placeholder}
          blurDataURL={blurDataURL}
          unoptimized={unoptimized}
          quality={quality}
         />
      </div>
    );
  }
);

Image.displayName = 'Image';

export { Image }; 